<template>
    <Card>
        <div class="card-body">
            <button
                class="btn btn-link"
                @click="$router.push({
                    name: 'OrderCreatePage',
                    query: {
                        orderStatusUuid: orderStatus.uuid
                    }
                })"
            >
                <i class="icon-plus3 mr-2"></i>
                {{ $t('buttons.create') }}
            </button>
        </div>
        <Table :columns="columns" :handle="tableHandle" :url="url" :orders="orders">
            <template #td-id="{row}">
                <router-link
                    :to="{
                        name: 'OrderProducts',
                        params: {
                            orderUuid: row.uuid
                        }
                    }">
                    {{ row.id }}
                </router-link>
                <a v-if="integrationServiceForOrderLinks.includes(row?.source?.integration?.service)" :href="generateIntegrationUrl(row)" class="d-block">{{ row.external_order_number }}</a>
                <span v-else-if="row.external_order_number" class="text-muted text-smaller d-block long-string" :title="row.external_order_number">{{ row.external_order_number }}</span>
            </template>
            <template #td-client="{row}">
                <AppNullableCell>
                    <template #default v-if="row.client">
                        {{ row.client.name }}
                        <br/><span class="text-muted">{{ row.client.phone || row.client.email }}</span>
                    </template>
                </AppNullableCell>
            </template>
            <template #td-source="{row}">
                <AppNullableCell>
                    <template #default v-if="row.source">
                        <img :src="row.source.image_file?.url || PLACEHOLDER_IMAGE_URL" class="source-image mr-1" />
                        {{ row.source.name }}
                    </template>
                </AppNullableCell>
            </template>
            <template #td-delivery="{row}">
                <AppNullableCell>
                    <template #default v-if="row.delivery">
                        <div class="d-flex align-items-center" style="max-width: 100%">
                            <img :src="getDeliveryIntegrationLogo(row.delivery.method)" class="source-image mr-2 flex-shrink-0" />
                            <div class="d-flex flex-column" style="overflow: hidden">
                                <span class="text-truncate text-body" :title="row.delivery.number || row.delivery.company_number || row.delivery.address">
                                    {{ row.delivery.number || row.delivery.company_number || row.delivery.address }}
                                </span>
                                <span v-if="row.delivery.status_label" class="text-muted text-smaller text-truncate" :title="row.delivery.status_label">
                                    {{ row.delivery.status_label }}
                                </span>
                            </div>
                        </div>
                    </template>
                </AppNullableCell>
            </template>
            <template #td-sum="{row}">
                {{ Price.factory(row.sum).toCurrencyString("UAH") }}
                <slot v-if="row.expenses_sum">
                    <br/><span class="text-danger">-{{ Price.factory(row.expenses_sum).toCurrencyString("UAH") }}</span>
                </slot>
            </template>
            <template #td-created_at="{row}">
                {{ DateFormatter.formatDateTime(row.created_at) }}
            </template>
            <template #td-closed_at="{row}">
                <AppNullableCell>
                    <template #default v-if="row.closed_at">
                        {{ DateFormatter.formatDateTime(row.closed_at) }}
                    </template>
                </AppNullableCell>
            </template>
        </Table>
    </Card>
</template>
<script>
import Card from "../../../../../components/new/CardComponent.vue";
import Table from "../../../../../components/new/table/FetchTable.vue";
import { createTableHandle } from "../../../../../components/new/table/tableHandle";
import DateFormatter from "../../../../../services/DateFormatter";
import Price from "../../../../../services/Price/Price";
import AppNullableCell from "../../../../components/tables/AppNullableCell.vue";
import { IntegrationServiceEnum } from "../../../../enums/IntegrationServiceEnum";
import { PLACEHOLDER_IMAGE_URL } from "../../../../constants";
import { getDeliveryIntegrationLogo } from "../../../../enums/DeliveryIntegrationsEnum";

export default {
    name: "OrdersTableCard",
    components: {
        Card, Table, AppNullableCell
    },
    props: {
        orderStatus: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            tableHandle: createTableHandle(),
            DateFormatter,
            Price,
            getDeliveryIntegrationLogo,
            PLACEHOLDER_IMAGE_URL,
            url: `/projects/${this.$route.params.projectUuid}/crm/orders`,
            orders: [{
                column: 'id',
                dir: 'desc'
            }],
            columns: [
                {key: 'id', name: this.$t('table.titles.id'), width: '150px', type: 'string', order: true},
                {key: 'client', name: this.$t('table.titles.client'), width: '200px', type: 'string', order: false},
                {key: 'source', name: this.$t('table.titles.source'), width: '150px', type: 'string', order: false},
                {key: 'delivery', name: this.$t('table.titles.delivery'), width: '300px', type: 'string', order: true},
                {key: 'sum', name: this.$t('table.titles.sum'), width: '150px', type: 'number', order: false},
                {key: 'created_at', name: this.$t('table.titles.created_at'), width: '150px', type: 'string', order: true},
                {key: 'closed_at', name: this.$t('table.titles.closed_at'), width: '150px', type: 'string', order: true},
            ],
            integrationServiceForOrderLinks: [
                IntegrationServiceEnum.SHOP_EXPRESS,
                IntegrationServiceEnum.WOOCOMMERCE,
                IntegrationServiceEnum.ROZETKA,
                IntegrationServiceEnum.EPICENTR,
                IntegrationServiceEnum.KASTA,
                IntegrationServiceEnum.PROM
            ]
        }
    },
    methods: {
        generateIntegrationUrl(row) {
             switch (row.source.integration.service) {
                case IntegrationServiceEnum.PROM:
                    return  `https://my.prom.ua/cms/order/edit/${row.external_order_number}`
                case IntegrationServiceEnum.ROZETKA:
                    return `https://seller.rozetka.com.ua/main/orders/all/${row.external_order_number}`
                case IntegrationServiceEnum.KASTA:
                    return `https://hub.kasta.ua/customer-orders/all?order=${row.external_order_number}`
                case IntegrationServiceEnum.EPICENTR:
                    return `https://admin.epicentrm.com.ua/oms/orders/${row.external_order_number}`
                case IntegrationServiceEnum.SHOP_EXPRESS:
                    return row.source.integration.api_credentials.domain ? `${row.source.integration.api_credentials.domain}/eco/ukr/shop/orders/edit/${row.external_order_number}` : null
                case IntegrationServiceEnum.WOOCOMMERCE:
                    return row.source.integration.api_credentials.domain ? `${row.source.integration.api_credentials.domain}/wp-admin/post.php?post=${row.external_order_number}&action=edit` : null
                default:
                    return null
            }
        },
    },
    mounted() {
        this.tableHandle.fetchRows({
            extra_fields: ["client", "delivery", "source.imageFile", "status"],
            status_uuids: [this.orderStatus.uuid]
        })
    }
}
</script>
<style scoped>
.delivery-address-string {
    width: 300px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
}
.source-image {
    border-radius: 5px;
    object-fit: cover;
    object-position: center;
    width: 20px;
    height: 20px;
}
.text-smaller {
    font-size: 12px;
    line-height: 18px;
}
.long-string {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
}
</style>